import { ProviderEvents, ErrorCode, OpenFeatureEventEmitter } from '@openfeature/web-sdk';
import equal from 'fast-deep-equal';
import { Value, Confidence } from '@spotify-confidence/sdk';

/**
 * OpenFeature Provider for Confidence Web SDK
 * @public
 */ class ConfidenceWebProvider {
    /** Initialize the Provider */ async initialize(context) {
        if (context) this.confidence.setContext(convertContext(context));
        let isStale = false;
        this.unsubscribe = this.confidence.subscribe((state)=>{
            if (state === 'READY') {
                if (isStale) {
                    this.events.emit(ProviderEvents.Ready);
                    this.events.emit(ProviderEvents.ConfigurationChanged);
                    isStale = false;
                }
            } else if (state === 'STALE') {
                this.events.emit(ProviderEvents.Stale);
                isStale = true;
            }
        });
        return this.expectReadyOrError();
    }
    /** Function called on closing of a Provider, handles unsubscribing from the Confidence SDK */ async onClose() {
        this.unsubscribe == null ? void 0 : this.unsubscribe.call(this);
        this.unsubscribe = undefined;
    }
    /** Called on Confidence Context change */ async onContextChange(oldContext, newContext) {
        const changes = contextChanges(oldContext, newContext);
        if (Object.keys(changes).length === 0) {
            return Promise.resolve();
        }
        this.confidence.setContext(convertContext(changes));
        return this.expectReadyOrError();
    }
    expectReadyOrError() {
        let close;
        return new Promise((resolve, reject)=>{
            close = this.confidence.subscribe((state)=>{
                if (state === 'READY') {
                    resolve();
                } else if (state === 'ERROR') {
                    reject(new Error('Provider initialization failed'));
                }
            });
        }).finally(close);
    }
    evaluateFlag(flagKey, defaultValue) {
        const evaluation = this.confidence.evaluateFlag(flagKey, defaultValue);
        if (evaluation.reason === 'ERROR') {
            const { errorCode, ...rest } = evaluation;
            return {
                ...rest,
                errorCode: this.mapErrorCode(errorCode)
            };
        }
        return evaluation;
    }
    mapErrorCode(errorCode) {
        switch(errorCode){
            case 'FLAG_NOT_FOUND':
                return ErrorCode.FLAG_NOT_FOUND;
            case 'TYPE_MISMATCH':
                return ErrorCode.TYPE_MISMATCH;
            case 'NOT_READY':
                return ErrorCode.PROVIDER_NOT_READY;
            default:
                return ErrorCode.GENERAL;
        }
    }
    /** Resolves with an evaluation of a Boolean flag */ resolveBooleanEvaluation(flagKey, defaultValue) {
        return this.evaluateFlag(flagKey, defaultValue);
    }
    /** Resolves with an evaluation of a Number flag */ resolveNumberEvaluation(flagKey, defaultValue) {
        return this.evaluateFlag(flagKey, defaultValue);
    }
    /** Resolves with an evaluation of an Object flag */ resolveObjectEvaluation(flagKey, defaultValue) {
        // this might throw but will be caught by OpenFeature
        Value.assertValue(defaultValue);
        return this.evaluateFlag(flagKey, defaultValue);
    }
    /** Resolves with an evaluation of a String flag */ resolveStringEvaluation(flagKey, defaultValue) {
        return this.evaluateFlag(flagKey, defaultValue);
    }
    constructor(confidence){
        /** Static data about the provider */ this.metadata = {
            name: 'ConfidenceWebProvider'
        };
        /** Events can be used by developers to track lifecycle events */ this.events = new OpenFeatureEventEmitter();
        this.confidence = confidence;
    }
}
function contextChanges(oldContext, newContext) {
    const uniqueKeys = new Set([
        ...Object.keys(newContext),
        ...Object.keys(oldContext)
    ]);
    const changes = {};
    for (const key of uniqueKeys){
        if (!equal(newContext[key], oldContext[key])) {
            if (key === 'targetingKey') {
                // targetingKey is a special case, it should never set to null but rather undefined
                changes[key] = newContext[key];
            } else {
                var _newContext_key;
                changes[key] = (_newContext_key = newContext[key]) != null ? _newContext_key : null;
            }
        }
    }
    return changes;
}
function convertContext({ targetingKey, ...context }) {
    const targetingContext = typeof targetingKey !== 'undefined' ? {
        targeting_key: targetingKey
    } : {};
    return {
        ...targetingContext,
        ...convertStruct(context)
    };
}
function convertValue(value) {
    if (typeof value === 'object') {
        if (value === null) return undefined;
        if (value instanceof Date) return value.toISOString();
        // @ts-expect-error TODO fix single type array conversion
        if (Array.isArray(value)) return value.map(convertValue);
        return convertStruct(value);
    }
    return value;
}
function convertStruct(value) {
    const struct = {};
    for (const key of Object.keys(value)){
        if (typeof value[key] === 'undefined') continue;
        struct[key] = convertValue(value[key]);
    }
    return struct;
}

/**
 * Creates an OpenFeature-adhering Confidence Provider
 * @param confidenceOrOptions - Confidence instance or options for Confidence Provider
 * @public */ function createConfidenceWebProvider(confidenceOrOptions) {
    if (confidenceOrOptions instanceof Confidence) {
        return new ConfidenceWebProvider(confidenceOrOptions);
    }
    const confidence = Confidence.create({
        ...confidenceOrOptions,
        environment: 'client'
    });
    return new ConfidenceWebProvider(confidence);
}

export { createConfidenceWebProvider };
//# sourceMappingURL=index.mjs.map
